import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal, ModalBody } from "reactstrap";
import styled from "styled-components";

import crossIcon from "../../assets/img/icons/cross_black.svg";
import { ReactComponent as LinkIcon } from "../../assets/img/icons/link_2.svg";
import androidIcon from "../../assets/img/onboardingImg/android.svg";
import iosIcon from "../../assets/img/onboardingImg/apple-13.svg";
import Confetti from "../../components/Confetti";
import { Icon } from "../../components/Icon/Icon";
import VideoPlayer from "../../components/VideoPlayer/VideoPlayer";
import { pushAdTrackingEvent } from "../../utils/adTracking";
import { PrimaryButton } from "../AuthNew/shared/authStyles";
import { AnimatePresence, motion } from "../AuthNew/shared/motionUtils";
import { ModalCrossIcon } from "../Dashboard/OnboardingModal/onboardingModalStyles";
import {
  ButtonGroup,
  CopyButton,
  Divider,
  EmailChip,
  EmailChipContainer,
  EmailInput,
  EmailStatus,
  InvalidCount,
  LinkInput,
  LinkInputGroup,
  LinkShareContainer,
  LinkShareDescription,
  LinkShareHeader,
  LinkShareIcon,
  LinkShareText,
  LinkShareTitle,
  ModalTitle,
  RemoveButton,
  SecondaryButton,
  SectionTitle,
  ValidCount,
} from "./shared/onboardingStyles";

const StyledModal = styled(Modal)`
  max-width: 650px;
  padding-top: 80px;

  @media screen and (min-width: 1600px) {
    padding-top: 100px;
  }
`;

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ isMember }) => (isMember ? "center" : "stretch")};
  text-align: ${({ isMember }) => (isMember ? "center" : "left")};
  padding: 45px 45px;
  background: white;
  border-radius: 12px;
  position: relative;
  width: 650px;
  min-height: 500px;

  @media screen and (max-width: 768px) {
    padding: 20px;
  }

  @media screen and (max-width: 576px) {
    width: 100%;
    max-width: 100%;
  }
`;

const VideoContainer = styled.div`
  margin-bottom: 32px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  cursor: pointer;

  .video-wrapper {
    border-radius: 8px;
    overflow: hidden;
    height: 320px;
  }

  .react-video-player {
    width: 100% !important;
    height: 320px !important;
    border: none !important;
  }

  &:hover {
    opacity: 0.95;
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 16px;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 16px;
  color: #7f8c8d;
  line-height: 1.6;
  margin-bottom: 32px;
  font-weight: 400;
`;

const HelperText = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-style: italic;
`;

const LimitMessage = styled(motion.div)`
  font-size: 13px;
  color: #059669;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  font-weight: 500;
`;

const LinkHelperText = styled(HelperText)`
  color: #9ca3af;
  margin-top: 12px;
  font-style: normal;
`;

// Success Screen Styles
const SuccessScreenContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 45px;
  min-height: 380px;
  position: relative;
  overflow: hidden;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const SuccessIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(to right, #2266ef 0%, #6c2ef2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  z-index: 1;
`;

const Checkmark = styled(motion.svg)`
  width: 40px;
  height: 40px;
  stroke: white;
  stroke-width: 3;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const GenericMessage = styled(motion.h2)`
  font-size: 32px;
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 8px;
  line-height: 1.3;

  @media screen and (max-width: 576px) {
    font-size: 24px;
  }
`;

const SpecificMessage = styled(motion.p)`
  font-size: 16px;
  color: #7f8c8d;
  margin-bottom: 0;
  line-height: 1.6;
`;

const ErrorMessage = styled.div`
  font-size: 14px;
  color: #ef4444;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DownloadButton = styled(PrimaryButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const DownloadIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const AppLinksFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  width: 100%;
`;

const AppLinksTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
  text-align: center;
`;

const AppLinksContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const AppLinkButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2266ef;
    background: #f9fafb;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const DOWNLOAD_LINKS = {
  android:
    "https://play.google.com/store/apps/details?id=com.fieldservicely.fieldservice.app",
  ios: "https://apps.apple.com/us/app/fieldservicely-app/id6472611827",
};

const OS_TYPES = {
  ANDROID: "android",
  IOS: "ios",
  UNKNOWN: "unknown",
};

const getOSAndArch = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  let os = OS_TYPES.UNKNOWN;

  if (userAgent.includes("android")) {
    os = OS_TYPES.ANDROID;
  } else if (
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("ipod") ||
    (userAgent.includes("mac") && "ontouchend" in document)
  ) {
    os = OS_TYPES.IOS;
  }

  return { os };
};

const PostOnboardingModal = ({
  isOpen,
  toggle,
  selectedOrganization,
  joinLink,
  userRole,
  organizationId,
  onInviteMember,
  isInviteLoading,
  memberInvited,
  clearMemberInvited,
  updateFirstLogin,
  userInfo,
}) => {
  const location = useLocation();
  const [copySuccess, setCopySuccess] = useState(false);
  const [emailChips, setEmailChips] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const emailInputRef = useRef(null);
  const [latestAppInfo, setLatestAppInfo] = useState(null);
  const [isLoadingAppInfo, setIsLoadingAppInfo] = useState(false);

  // Success screen state management
  const [currentView, setCurrentView] = useState("action");
  const [successData, setSuccessData] = useState({ generic: "", specific: "" });
  const [showConfetti, setShowConfetti] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [pendingView, setPendingView] = useState(null); // View to show after exit completes
  const autoCloseTimeoutRef = useRef(null);

  const isFirstLogin = localStorage.getItem("is_first_login") === "true";
  const isAdminOrOwner = userRole === "admin" || userRole === "owner";
  const { os } = useMemo(() => getOSAndArch(), []);

  const fetchLatestAppInfo = useCallback(async () => {
    if (os === OS_TYPES.UNKNOWN) return null;

    try {
      setIsLoadingAppInfo(true);
    } catch (error) {
      return null;
    } finally {
      setIsLoadingAppInfo(false);
    }
  }, [os]);

  const getDownloadInfo = useMemo(() => {
    const downloadLink = DOWNLOAD_LINKS[os] || DOWNLOAD_LINKS.android;

    const osConfigs = {
      android: {
        link: downloadLink,
        text: "Download Android App",
        icon: androidIcon,
        description:
          "Install our Android app now, select a project and start your timer",
      },
      ios: {
        link: downloadLink,
        text: "Download iOS App",
        icon: iosIcon,
        description:
          "Install our iOS app now, select a project and start your timer",
      },
      default: {
        link: DOWNLOAD_LINKS.android,
        text: "Download Mobile App",
        icon: androidIcon,
        description:
          "Install our mobile app now, select a job and start your work",
      },
    };

    if (os === OS_TYPES.ANDROID) return osConfigs.android;
    if (os === OS_TYPES.IOS) return osConfigs.ios;
    return osConfigs.default;
  }, [os]);

  // Email validation
  const isValidEmail = useCallback(email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }, []);

  // Add email to chips
  const addEmailChip = useCallback(
    (email, shouldClearInput = true) => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) return false;

      let wasAdded = false;

      // Check if we've reached the maximum limit of 5 emails
      setEmailChips(prev => {
        if (prev.length >= 5) {
          return prev; // Don't add more emails if limit reached
        }

        // Check for duplicate emails (case-insensitive)
        const emailLower = trimmedEmail.toLowerCase();
        const isDuplicate = prev.some(
          chip => chip.email.toLowerCase() === emailLower,
        );

        if (isDuplicate) {
          setInviteError(
            `"${trimmedEmail}" is already in the list. Please enter a different email.`,
          );
          setTimeout(() => setInviteError(null), 4000);
          return prev; // Don't add duplicate email
        }

        const isValid = isValidEmail(trimmedEmail);
        const newChip = {
          id: Date.now() + Math.random(),
          email: trimmedEmail,
          isValid,
        };
        wasAdded = true;
        return [...prev, newChip];
      });

      // Only clear input if email was successfully added
      if (wasAdded && shouldClearInput) {
        setEmailInput("");
      }

      return wasAdded;
    },
    [isValidEmail],
  );

  // Remove email chip
  const removeEmailChip = useCallback(chipId => {
    setEmailChips(prev => prev.filter(chip => chip.id !== chipId));
  }, []);

  // Handle input change
  const handleInputChange = useCallback(
    e => {
      // Prevent typing if limit reached
      if (emailChips.length >= 5) {
        return;
      }
      setEmailInput(e.target.value);
    },
    [emailChips.length],
  );

  // Handle paste event to detect if user pastes the join link
  const handlePaste = useCallback(
    e => {
      // Prevent pasting if limit reached
      if (emailChips.length >= 5) {
        e.preventDefault();
        return;
      }

      const pastedText = e.clipboardData.getData("text");
      // Check if pasted text contains the join link
      if (joinLink && pastedText.includes(joinLink)) {
        e.preventDefault();
        setInviteError(
          "This is your join link! Share it via email or messaging apps instead. Enter email addresses below to send direct invitations.",
        );
        setTimeout(() => setInviteError(null), 5000);
      }
    },
    [joinLink, emailChips.length],
  );

  // Handle input key events
  const handleInputKeyDown = useCallback(
    e => {
      // Prevent adding emails if limit reached
      if (emailChips.length >= 5) {
        return;
      }

      if (
        e.key === "Enter" ||
        e.key === "," ||
        e.key === ";" ||
        e.key === " "
      ) {
        e.preventDefault();
        if (emailInput.trim()) {
          addEmailChip(emailInput);
        }
      } else if (
        e.key === "Backspace" &&
        !emailInput &&
        emailChips.length > 0
      ) {
        setEmailChips(prev => prev.slice(0, -1));
      }
    },
    [emailInput, addEmailChip, emailChips.length],
  );

  // Auto-process emails when input changes
  useEffect(() => {
    if (emailChips.length >= 5) {
      return; // Don't process if limit reached
    }

    if (emailInput.match(/[,;\s]/)) {
      const emails = emailInput.split(/[,;\s]+/).filter(e => e.trim());
      if (emails.length === 0) return;

      // Check for duplicates first before processing
      const duplicateEmails = emails.filter(email => {
        const emailLower = email.trim().toLowerCase();
        return emailChips.some(chip => chip.email.toLowerCase() === emailLower);
      });

      // If there's a duplicate, show error and remove the separator to prevent re-processing
      if (duplicateEmails.length > 0) {
        const duplicateEmail = duplicateEmails[0].trim();
        setInviteError(
          `"${duplicateEmail}" is already in the list. Please enter a different email.`,
        );
        setTimeout(() => setInviteError(null), 4000);

        // Remove the separator that triggered processing to prevent re-triggering
        // This keeps the duplicate email visible in the input
        const lastSeparatorIndex = Math.max(
          emailInput.lastIndexOf(","),
          emailInput.lastIndexOf(";"),
          emailInput.lastIndexOf(" "),
        );
        if (lastSeparatorIndex !== -1) {
          // Keep text before the separator (the duplicate email)
          const newInput = emailInput.substring(0, lastSeparatorIndex).trim();
          setEmailInput(newInput);
        }
        return; // Don't process any emails if there's a duplicate
      }

      // No duplicates, process all emails
      // Batch add all emails at once to avoid multiple re-renders
      setEmailChips(prev => {
        if (prev.length >= 5) {
          return prev;
        }

        const remainingSlots = 5 - prev.length;
        const emailsToAdd = emails.slice(0, remainingSlots);

        const newChips = emailsToAdd.map((email, index) => {
          const trimmedEmail = email.trim();
          const isValid = isValidEmail(trimmedEmail);
          return {
            id: Date.now() + Math.random() * (index + 1) + index,
            email: trimmedEmail,
            isValid,
          };
        });

        return [...prev, ...newChips];
      });

      // Clear input after successful processing
      setEmailInput("");
    }
  }, [emailInput, emailChips, isValidEmail]);

  // Get valid and invalid emails from chips
  const validEmails = emailChips
    .filter(chip => chip.isValid)
    .map(chip => chip.email);
  const invalidEmails = emailChips
    .filter(chip => !chip.isValid)
    .map(chip => chip.email);
  const hasValidEmails = validEmails.length > 0;

  // Show success screen helper function
  const showSuccessScreen = useCallback(
    (genericMessage, specificMessage) => {
      // Update first login flag FIRST before any UI changes
      if (isFirstLogin && updateFirstLogin) {
        updateFirstLogin();
      }

      setSuccessData({ generic: genericMessage, specific: specificMessage });

      // Start confetti immediately
      setShowConfetti(true);

      // Store the pending view - will be applied when exit completes
      setPendingView("success");

      // Trigger exit by removing current view from render
      setCurrentView(null);

      // Clear any existing timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }

      // Auto-close after 4.5 seconds (give users time to see the success message)
      autoCloseTimeoutRef.current = setTimeout(() => {
        toggle();
      }, 4500);
    },
    [isFirstLogin, updateFirstLogin, toggle, currentView],
  );

  // Handle exit complete - show pending view
  const handleExitComplete = useCallback(() => {
    if (pendingView) {
      setCurrentView(pendingView);
      setPendingView(null);
    }
  }, [pendingView]);

  const handleCopyLink = useCallback(async () => {
    if (!joinLink) return;
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopySuccess(true);

      // Transition to success screen immediately
      showSuccessScreen(
        "All set!",
        "Link copied! Time to share it with your team",
      );
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [joinLink, showSuccessScreen]);

  const handleSkip = useCallback(() => {
    showSuccessScreen("All set!", "You're ready to go");
  }, [showSuccessScreen]);

  const handleSendInvitations = useCallback(async () => {
    const isInputValid = emailInput && isValidEmail(emailInput);
    const canSend = hasValidEmails || isInputValid;

    if (!canSend || !onInviteMember || !selectedOrganization?.id) {
      return;
    }

    // Clear any previous errors
    setInviteError(null);

    // Prepare final list of emails
    let finalEmails = [...validEmails];

    // If there's a valid email in the input that isn't in the list, add it
    if (isInputValid) {
      const trimmedInput = emailInput.trim();
      // Check if it's already in the list (case-insensitive)
      const isDuplicate = finalEmails.some(
        email => email.toLowerCase() === trimmedInput.toLowerCase(),
      );

      if (!isDuplicate) {
        finalEmails.push(trimmedInput);
      }
    }

    if (finalEmails.length === 0) return;

    // Track team invitation step
    pushAdTrackingEvent({
      event: "onboarding_team_invited",
      location,
      selectedOrganization,
      user_id: userInfo?.user_id || null,
      full_name:
        userInfo?.first_name && userInfo?.last_name
          ? `${userInfo.first_name} ${userInfo.last_name}`
          : userInfo?.first_name || null,
      email: userInfo?.email || null,
    });

    // Call onInviteMember (it's a Redux action)
    onInviteMember({
      organization_id: selectedOrganization?.id,
      emails: finalEmails,
    });
  }, [
    hasValidEmails,
    validEmails,
    emailInput,
    isValidEmail,
    onInviteMember,
    selectedOrganization,
    location,
    userInfo,
  ]);

  const handleDownload = useCallback(() => {
    window.open(getDownloadInfo.link, "_blank");
    showSuccessScreen("All set!", "Download started!");
  }, [getDownloadInfo.link, showSuccessScreen]);

  // Fetch app info for members
  useEffect(() => {
    if (isOpen && !isAdminOrOwner) {
      fetchLatestAppInfo();
    }
  }, [isOpen, isAdminOrOwner, fetchLatestAppInfo]);

  // Handler for app download
  const handleAppDownload = useCallback(
    (link, icon) => {
      window.open(link, "_blank");
      showSuccessScreen("All set!", "Download started!");
    },
    [showSuccessScreen],
  );

  // Handler for backdrop click
  const handleBackdropClick = useCallback(() => {
    showSuccessScreen("All set!", "You're ready to go");
  }, [showSuccessScreen]);

  // Success Screen Render Function
  const renderSuccessScreen = () => (
    <>
      <SuccessScreenContainer
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
      >
        <SuccessIcon
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          transition={{
            duration: 0.6,
            times: [0, 0.6, 1],
            ease: "easeOut",
          }}
        >
          <Checkmark
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 0.4,
              delay: 0.3,
              ease: "easeOut",
            }}
            viewBox="0 0 24 24"
          >
            <motion.path
              d="M5 13l4 4L19 7"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: 0.4,
                delay: 0.3,
                ease: "easeOut",
              }}
            />
          </Checkmark>
        </SuccessIcon>

        <GenericMessage
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          {successData.generic || "All set!"}
        </GenericMessage>

        <SpecificMessage
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}
        >
          {successData.specific || "You're all set!"}
        </SpecificMessage>
      </SuccessScreenContainer>
    </>
  );
  const orgType = selectedOrganization?.type || "operation";

  // Member Content Render Function
  const renderMemberContent = () => (
    <>
      <VideoContainer>
        <VideoPlayer
          style={{ border: "none" }}
          video={
            orgType === "service"
              ? "https://youtu.be/DEi6xR6GP1Q"
              : "https://youtu.be/JK5T-kwlPf8"
          }
          playing={true}
          muted={true}
          onPlay={() => {
            // do nothing
          }}
        />
      </VideoContainer>
      <Title>You're Good to Go!</Title>
      <Description>
        Install our mobile app now, select a job and start your work
      </Description>

      <AppLinksFooter>
        <AppLinksTitle>Download our apps</AppLinksTitle>
        <AppLinksContainer>
          <AppLinkButton
            onClick={() =>
              handleAppDownload(DOWNLOAD_LINKS.android, androidIcon)
            }
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img src={androidIcon} alt="Android" />
            Android
          </AppLinkButton>
          <AppLinkButton
            onClick={() => handleAppDownload(DOWNLOAD_LINKS.ios, iosIcon)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img src={iosIcon} alt="iOS" />
            iOS
          </AppLinkButton>
        </AppLinksContainer>
      </AppLinksFooter>
    </>
  );

  // Update the useEffect that sets initial view (around line 660)
  useEffect(() => {
    if (isOpen) {
      // Show member content (action view) when modal opens
      setCurrentView("action");
      setSuccessData({
        generic: "All set!",
        specific: "You're ready to go",
      });
      setShowConfetti(false); // Don't show confetti until success
      setInviteError(null);
      setCopySuccess(false);

      // Clear any existing timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    } else {
      // Clear timeout on close
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
      setShowConfetti(false);
    }
  }, [isOpen]);

  // Update the return statement to show action view and handle backdrop (around line 1001)
  return (
    <>
      {/* Render confetti when triggered */}
      {showConfetti && <Confetti />}
      <StyledModal isOpen={isOpen} toggle={handleBackdropClick} backdrop={true}>
        <ModalBody style={{ padding: "0" }}>
          <ModalContentWrapper isMember={true}>
            {/* Close button - positioned at modal level */}
            {currentView === "action" && (
              <ModalCrossIcon
                onClick={handleBackdropClick}
                src={crossIcon}
                alt="Close"
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  zIndex: 10,
                }}
              />
            )}
            {/* Screen transition - wrapper to contain absolute positioned views */}
            <div
              style={{
                position: "relative",
                width: "100%",
                minHeight: "400px",
              }}
            >
              <AnimatePresence
                mode="wait"
                initial={false}
                onExitComplete={handleExitComplete}
              >
                {currentView === "action" && (
                  <motion.div
                    key="action"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeIn" }}
                  >
                    {renderMemberContent()}
                  </motion.div>
                )}
                {currentView === "success" && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeIn" }}
                  >
                    {renderSuccessScreen()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ModalContentWrapper>
        </ModalBody>
      </StyledModal>
    </>
  );
};

export default PostOnboardingModal;
